import {
  Captions,
  CheckCheck,
  ChevronDown,
  FileText,
  Forward,
  ReplyAll,
} from "lucide-react";
import { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";

import { formatText } from "../../../utils/messageHelpers";

import ImagePopup from "../../ImagePopup";
import CallTranscriptModal from "../../calls/CallTranscriptModal";
import QuotedMessage from "./QuotedMessage";
import { getDayLabel } from "../../../utils/helpers";

// Registro de ligação atendida pelo CRM ("… · duração mm:ss"): só essas podem
// ter transcrição (no celular o CRM não recebe o áudio).
const transcribableCallId = (msg) =>
  msg.messageId?.startsWith("call_") && /duração \d+:\d{2}/.test(msg.messageBody || "")
    ? msg.messageId.slice(5)
    : null;

const MessageItem = memo(
  ({ msg, contact, onReplyMessage, onForwardMessage, setSelectedImage, onShowTranscript }) => {
    const [showTranscription, setShowTranscription] = useState(false);

    const isSent = msg.direction === "OUTGOING";
    const callId = transcribableCallId(msg);

    const toggleTranscription = useCallback(() => {
      setShowTranscription((prev) => !prev);
    }, []);

    return (
      <div className={`flex ${isSent ? "justify-end" : "justify-start"}`}>
        <div
          className={`max-w-md rounded-lg px-4 py-2 ${
            isSent
              ? "bg-green-500 text-white"
              : "bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100"
          }`}
        >
          {msg.quoteMessage && (
            <QuotedMessage
              contact={contact}
              quote={msg.quoteMessage}
              isSent={isSent}
            />
          )}

          {msg.messageType === "image" && (
            <div className="flex flex-col items-start space-y-2">
              <img
                src={`${process.env.REACT_APP_URL}${msg.mediaPath}`}
                loading="lazy"
                alt="imagem enviada"
                className="max-w-[220px] cursor-pointer rounded-lg transition hover:opacity-90"
                onClick={() =>
                  setSelectedImage(
                    `${process.env.REACT_APP_URL}${msg.mediaPath}`,
                  )
                }
              />
              {msg.messageBody && (
                <p className="whitespace-pre-wrap break-words text-sm">
                  {formatText(msg.messageBody)}
                </p>
              )}
            </div>
          )}

          {msg.messageType === "audio" && (
            <div className="flex flex-col items-start space-y-2">
              <audio controls preload="none" className="w-48">
                <source src={`${process.env.REACT_APP_URL}${msg.mediaPath}`} />
              </audio>

              {showTranscription && (
                <p className="whitespace-pre-wrap break-words text-sm italic opacity-90">
                  {formatText(msg.messageBody)}
                </p>
              )}
            </div>
          )}

          {msg.messageType === "document" && (
            <div className="flex flex-col items-start space-y-2">
              <a
                href={`${process.env.REACT_APP_URL}${msg.mediaPath}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white underline hover:bg-white/20 dark:text-green-300"
              >
                {`${msg.mediaPath}`?.split("/").pop() || "Documento"}
              </a>
              {msg.messageBody && (
                <p className="whitespace-pre-wrap break-words text-sm">
                  {formatText(msg.messageBody)}
                </p>
              )}
            </div>
          )}

          {msg.messageType === "video" && (
            <div className="flex flex-col items-start space-y-2">
              <video
                controls
                preload="none"
                className="max-w-[220px] rounded-lg"
                src={`${process.env.REACT_APP_URL}${msg.mediaPath}`}
              />
              {msg.messageBody && (
                <p className="whitespace-pre-wrap break-words text-sm">
                  {formatText(msg.messageBody)}
                </p>
              )}
            </div>
          )}

          {!["image", "audio", "document", "video"].includes(
            msg.messageType,
          ) && (
            <p className="whitespace-pre-wrap break-words text-sm">
              {formatText(msg.messageBody)}
            </p>
          )}

          <div
            className={`mt-1 flex items-center justify-end space-x-1 text-xs ${
              isSent ? "text-green-100" : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {msg.messageType === "audio" && (
              <button onClick={toggleTranscription} className="text-xs">
                <Captions className="h-4 w-4" />
              </button>
            )}

            {callId && (
              <button
                onClick={() => onShowTranscript(callId)}
                title="Ver transcrição da ligação"
                className="flex items-center gap-1 text-xs underline-offset-2 hover:underline"
              >
                <FileText className="h-4 w-4" />
                Ver transcrição
              </button>
            )}

            <button
              onClick={() => onReplyMessage(msg.messageId)}
              className="text-xs"
            >
              <ReplyAll className="h-4 w-4" />
            </button>

            <button
              onClick={() => onForwardMessage(msg.messageId)}
              className="text-xs"
            >
              <Forward className="h-4 w-4" />
            </button>

            <span className="pl-2">
              {msg.sentAt
                ? new Date(msg.sentAt).toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : ""}
            </span>

            {isSent && (
              <CheckCheck
                className={`${msg.isRead ? "text-blue-600" : ""} h-4 w-4`}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
);

const ChatMessages = memo(
  ({
    messages,
    contact,
    conversationId,
    sendAction,
    onReplyMessage,
    onForwardMessage,
  }) => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [transcriptCallId, setTranscriptCallId] = useState(null);
    const closeTranscript = useCallback(() => setTranscriptCallId(null), []);

    const messagesEndRef = useRef(null);

    const groupedMessages = useMemo(() => {
      const groups = {};

      for (const msg of messages) {
        if (!msg.sentAt) continue;

        const date = getDayLabel(msg.sentAt);

        if (!groups[date]) {
          groups[date] = [];
        }

        groups[date].push(msg);
      }

      return Object.entries(groups);
    }, [messages]);

    const loadOlderMessages = useCallback(() => {
      if (!conversationId) return;

      sendAction("load_older_messages", {
        conversationId,
        beforeMessageId: messages[0]?.messageId,
        limit: 100,
      });
    }, [conversationId, messages, sendAction]);

    const scrollToBottom = useCallback(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
      messagesEndRef.current?.scrollIntoView();
    }, [messages.length]);

    return (
      <div className="relative flex-1 overflow-y-auto bg-gray-100 p-4 dark:bg-gray-900">
        <button
          onClick={loadOlderMessages}
          className="mx-auto mb-4 block rounded-full bg-gray-300 px-4 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        >
          Carregar mensagens anteriores
        </button>

        {groupedMessages.map(([date, msgs]) => (
          <div className="space-y-4" key={date}>
            <div className="flex justify-center">
              <span className="rounded-full bg-gray-300 px-3 py-1 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                {date}
              </span>
            </div>

            {msgs.map((msg) => (
              <MessageItem
                key={msg.id}
                msg={msg}
                contact={contact}
                onReplyMessage={onReplyMessage}
                onForwardMessage={onForwardMessage}
                setSelectedImage={setSelectedImage}
                onShowTranscript={setTranscriptCallId}
              />
            ))}
          </div>
        ))}

        <div ref={messagesEndRef} />

        <ImagePopup
          imageUrl={selectedImage}
          onClose={() => setSelectedImage(null)}
        />

        {transcriptCallId && (
          <CallTranscriptModal callId={transcriptCallId} onClose={closeTranscript} />
        )}

        <button
          onClick={scrollToBottom}
          className="sticky bottom-2 mx-auto block rounded-full bg-gray-300 p-2 text-gray-700 transition hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        >
          <ChevronDown />
        </button>
      </div>
    );
  },
);

export default ChatMessages;
